/* ══════════════════════════════════════════════════════════════════════
   INSURGENT BRANDS sheet

   Shape (header row sits a few rows down, under a banner):
     Bottler | DDR | SPK+ED Industry Vol UC | Total Insurgent Share Mar (%)
             | Lead Insurgent WD Mar (%)

   Insurgent = Campa + Artos + Dailee + Tilo + Davat + Bindu + Lahori.
   Bubble encoding: x = Lead Insurgent WD, y = Total Insurgent Share,
   size = SPK+ED Industry Volume.
   ══════════════════════════════════════════════════════════════════════ */

export const INSURGENT_SHEET = /insurgent|bubble|spk.*ed|wd.*share/i;

/** "59.90%" -> 0.599 ; 0.599 -> 0.599 ; 59.9 -> 0.599 */
export function parsePct(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v > 1.5 ? v / 100 : v;
  const s = String(v).trim();
  const n = Number(s.replace(/[%,\s]/g, ''));
  if (isNaN(n)) return null;
  return s.includes('%') || n > 1.5 ? n / 100 : n;
}

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

export function parseInsurgentSheet(raw) {
  // The header is buried under a banner row, so scan for the row holding
  // both "Bottler" and "DDR".
  let headerIdx = -1;
  for (let i = 0; i < Math.min(20, raw.length); i++) {
    const row = (raw[i] || []).map(c => String(c ?? '').trim().toLowerCase());
    if (row.some(c => c === 'bottler') && row.some(c => c === 'ddr')) { headerIdx = i; break; }
  }
  if (headerIdx < 0) return null;

  const headers = (raw[headerIdx] || []).map(h => String(h ?? '').trim());
  const find = (re) => headers.findIndex(h => re.test(h));

  const iBottler = find(/^bottler$/i);
  const iDdr     = find(/^ddr$/i);
  const iVol     = find(/spk.*ed.*(vol|uc)|industry\s*vol/i);
  const iShare   = find(/insurgent\s*share/i);
  const iWd      = find(/insurgent\s*wd|lead\s*insurgent/i);

  if (iDdr < 0 || iShare < 0 || iWd < 0) return null;

  const rows = [];
  for (let i = headerIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    if (!r || !r.some(c => c !== null && c !== undefined && c !== '')) continue;
    const ddr = r[iDdr];
    if (typeof ddr !== 'string' || !ddr.trim()) continue;
    if (/^(total|all|grand\s*total)$/i.test(ddr.trim())) continue;

    const share = parsePct(r[iShare]);
    const wd    = parsePct(r[iWd]);
    if (share == null && wd == null) continue;

    rows.push({
      bottler: iBottler >= 0 ? String(r[iBottler] ?? '').trim() : '',
      ddr: ddr.trim(),
      vol: iVol >= 0 ? num(r[iVol]) : 0,
      share: share ?? 0,      // y
      wd: wd ?? 0,            // x
    });
  }
  if (!rows.length) return null;

  const med = (arr) => {
    if (!arr.length) return 0;
    const a = [...arr].sort((p, q) => p - q);
    return a[Math.floor(a.length / 2)];
  };

  return {
    rows,
    bottlers: [...new Set(rows.map(r => r.bottler).filter(Boolean))].sort(),
    medShare: med(rows.map(r => r.share)),
    medWd: med(rows.map(r => r.wd)),
    headerLabels: {
      vol: iVol >= 0 ? headers[iVol] : 'Industry Vol UC',
      share: headers[iShare] || 'Total Insurgent Share',
      wd: headers[iWd] || 'Lead Insurgent WD',
    },
  };
}

export function findInsurgentSheet(workbook, XLSX) {
  for (const name of workbook.SheetNames) {
    if (!INSURGENT_SHEET.test(name)) continue;
    const raw = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: true, defval: null });
    const parsed = parseInsurgentSheet(raw);
    if (parsed) return { ...parsed, sheetName: name };
  }
  // Name didn't match — probe every sheet for the Bottler+DDR header
  for (const name of workbook.SheetNames) {
    const raw = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw: true, defval: null });
    const parsed = parseInsurgentSheet(raw);
    if (parsed) return { ...parsed, sheetName: name };
  }
  return null;
}